/**
 * ImportProgressBanner — shows a progress bar for background Flow360 imports.
 *
 * Placed at the top of the project view. It either uses a `jobId` from URL
 * search params or auto-discovers active imports for the project.
 *
 * Polls GET /api/flow360/import/{jobId}/progress every 2s while running.
 * On completion, invalidates the run-table query so data appears automatically.
 */

import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchImportProgress, fetchActiveImports } from "../../flow360/api";

interface Props {
  projectId: string;
  jobId?: string;
}

function formatCount(n: number): string {
  return n.toLocaleString();
}

export function ImportProgressBanner({ projectId, jobId: initialJobId }: Props) {
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);
  const [completedDismissTimer, setCompletedDismissTimer] = useState(false);

  // Auto-discover active imports if no jobId provided
  const { data: activeImports } = useQuery({
    queryKey: ["active-imports", projectId],
    queryFn: () => fetchActiveImports(projectId),
    enabled: !initialJobId,
    refetchInterval: 10_000,
  });

  const jobId = initialJobId || activeImports?.[0]?.job_id;

  // Poll progress
  const { data: progress } = useQuery({
    queryKey: ["import-progress", jobId],
    queryFn: () => fetchImportProgress(jobId!),
    enabled: !!jobId && !dismissed,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "completed" || status === "failed") return false;
      return 2000;
    },
  });

  // On completion, invalidate run-table and auto-dismiss after 5s
  useEffect(() => {
    if (progress?.status === "completed" && !completedDismissTimer) {
      setCompletedDismissTimer(true);
      queryClient.invalidateQueries({ queryKey: ["run-table", projectId] });
      queryClient.invalidateQueries({ queryKey: ["provenance", projectId] });
      queryClient.invalidateQueries({ queryKey: ["nodes", projectId] });
      const timer = setTimeout(() => setDismissed(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [progress?.status, completedDismissTimer, projectId, queryClient]);

  // Reset dismissed state when jobId changes
  useEffect(() => {
    setDismissed(false);
    setCompletedDismissTimer(false);
  }, [jobId]);

  if (dismissed || !jobId || !progress) return null;

  const { status, progress_pct, imported_cases, total_cases, skipped_cases, nodes_created, error_message } = progress;

  const isPending = status === "pending";
  const isRunning = status === "running";
  const isCompleted = status === "completed";
  const isFailed = status === "failed";

  const barColor = isFailed
    ? "var(--red-9)"
    : isCompleted
      ? "var(--green-9)"
      : "var(--accent-9)";

  const bgColor = isFailed
    ? "var(--red-a3)"
    : isCompleted
      ? "var(--green-a3)"
      : "var(--gray-a2)";

  let statusText = "";
  if (isPending) statusText = "Preparing import...";
  if (isRunning) statusText = "Importing from Flow360...";
  if (isCompleted) {
    statusText = `Import complete -- ${formatCount(imported_cases)} cases imported`;
    if (skipped_cases > 0) statusText += `, ${formatCount(skipped_cases)} skipped`;
    statusText += `, ${formatCount(nodes_created)} nodes created`;
  }
  if (isFailed) statusText = `Import failed: ${error_message || "Unknown error"}`;

  const pct = Math.max(0, Math.min(100, progress_pct));

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        height: 40,
        padding: "0 16px",
        background: bgColor,
        borderBottom: "1px solid var(--gray-a4)",
        flexShrink: 0,
        fontSize: 12,
        fontFamily: "inherit",
        color: "var(--gray-12)",
      }}
    >
      {/* Status text */}
      <span
        style={{
          whiteSpace: "nowrap",
          fontWeight: 500,
          color: isFailed ? "var(--red-11)" : isCompleted ? "var(--green-11)" : "var(--gray-12)",
          flexShrink: 0,
        }}
      >
        {statusText}
      </span>

      {/* Progress bar */}
      {(isPending || isRunning || isCompleted) && (
        <div
          style={{
            flex: 1,
            minWidth: 120,
            maxWidth: 320,
            height: 6,
            borderRadius: 3,
            background: "var(--gray-a5)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              height: "100%",
              borderRadius: 3,
              background: barColor,
              width: isPending ? "100%" : `${pct}%`,
              transition: isRunning ? "width 0.5s ease" : undefined,
              ...(isPending
                ? {
                    animation: "importPulse 1.5s ease-in-out infinite",
                    opacity: 0.6,
                  }
                : {}),
            }}
          />
        </div>
      )}

      {/* Percentage + counts */}
      {isRunning && (
        <span style={{ whiteSpace: "nowrap", color: "var(--gray-11)", flexShrink: 0 }}>
          {pct}%
          {total_cases > 0 && (
            <span style={{ marginLeft: 8 }}>
              {formatCount(imported_cases)} / {formatCount(total_cases)} cases
            </span>
          )}
        </span>
      )}

      {/* Dismiss button for failed state */}
      {isFailed && (
        <button
          onClick={() => setDismissed(true)}
          style={{
            marginLeft: "auto",
            padding: "2px 8px",
            fontSize: 11,
            border: "1px solid var(--gray-a6)",
            borderRadius: 4,
            background: "transparent",
            color: "var(--gray-11)",
            cursor: "pointer",
            fontFamily: "inherit",
            flexShrink: 0,
          }}
        >
          Dismiss
        </button>
      )}

      {/* Inline keyframes for the pending pulse animation */}
      <style>{`
        @keyframes importPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </div>
  );
}
